import { useMemo, useState } from 'react';
import { Ban, ShieldAlert, Search, X, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBanListQuery } from '@/client/api/messaging/messaging.query';
import {
  useCreateBanMutation,
  useLiftBanMutation,
} from '@/client/api/messaging/messaging.mutation';
import {
  BanListParams,
  formatEpoch,
  MessagingBan,
} from '@/client/types/messaging';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  OrgPartyLink,
  UserPartyLink,
} from '@/client/components/admin/Messaging/PartyHoverCard';

interface BanFilters {
  active: string;
  subject_type: string;
  scope: string;
  expired: string;
  sort: string;
  order: string;
}

const selectCls =
  'min-w-[130px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing bans requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">message.view</code> permission.
    </p>
  </div>
);

const CreateBanDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) => {
  const createBan = useCreateBanMutation();
  const [subjectType, setSubjectType] = useState<'user' | 'org'>('user');
  const [subjectId, setSubjectId] = useState('');
  const [scope, setScope] = useState('outbound');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const submit = async () => {
    const id = Number(subjectId.trim());
    if (!Number.isFinite(id) || id <= 0) {
      toast.error('Enter a valid subject ID', { richColors: true });
      return;
    }
    if (!reason.trim()) {
      toast.error('Reason is required', { richColors: true });
      return;
    }
    let expires: number | undefined;
    if (expiresAt.trim()) {
      const ms = Date.parse(expiresAt);
      if (!Number.isFinite(ms)) {
        toast.error('Invalid expiry datetime', { richColors: true });
        return;
      }
      expires = Math.floor(ms / 1000);
    }
    try {
      await createBan.mutateAsync({
        body: {
          subject_type: subjectType,
          subject_id: id,
          scope,
          reason: reason.trim(),
          ...(expires != null ? { expires_at: expires } : {}),
        },
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success('Ban created', { richColors: true });
      setSubjectId('');
      setReason('');
      setExpiresAt('');
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create ban', { richColors: true });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create messaging ban</DialogTitle>
          <DialogDescription>
            Stops the subject from sending messages. Takes effect within ~30s on live paths.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-gray-500">Subject type</label>
              <select
                value={subjectType}
                onChange={(e) => setSubjectType(e.target.value as 'user' | 'org')}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <option value="user">User</option>
                <option value="org">Organization</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">
                {subjectType === 'user' ? 'UID' : 'OID'}
              </label>
              <input
                type="number"
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">Scope</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="all">All</option>
              <option value="outbound">Outbound</option>
              <option value="dm">DM</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="harassment"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">Expires (optional)</label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={createBan.isPending} onClick={submit}>
            {createBan.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Create ban
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MessagingBansPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: '/admin/communications/messaging' },
    { label: 'Messaging' },
    { label: 'Bans' },
  ]);

  const viewPerm = useHasPermission('message.view');
  const banPerm = useHasPermission('message.ban');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canBan = banPerm.isSuperAdmin || banPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [createOpen, setCreateOpen] = useState(false);
  const liftBan = useLiftBanMutation();

  const {
    searchTerm,
    debouncedSearchTerm,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    resetFilters,
  } = useListFilters<BanFilters>({
    initialFilters: {
      active: 'true',
      subject_type: '',
      scope: '',
      expired: '',
      sort: 'created_at',
      order: 'desc',
    },
    initialLimit: 25,
  });

  const listParams: BanListParams = useMemo(() => {
    const q = debouncedSearchTerm.trim();
    const asId = q && /^\d+$/.test(q) ? Number(q) : undefined;
    return {
      limit,
      offset: (page - 1) * limit,
      sort: filters.sort || 'created_at',
      order: filters.order || 'desc',
      ...(filters.active === 'true' || filters.active === 'false'
        ? { active: filters.active }
        : {}),
      ...(filters.subject_type ? { subject_type: filters.subject_type } : {}),
      ...(filters.scope ? { scope: filters.scope } : {}),
      ...(filters.expired === 'true' || filters.expired === 'false'
        ? { expired: filters.expired }
        : {}),
      ...(asId != null ? { subject_id: asId } : {}),
    };
  }, [debouncedSearchTerm, filters, page, limit]);

  const { data, isLoading, isError, error } = useBanListQuery(
    listParams,
    canView && !permLoading,
  );

  const bans = data?.bans ?? [];
  const pagination = data?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);
  const hasFilters =
    !!searchTerm ||
    filters.active !== 'true' ||
    !!filters.subject_type ||
    !!filters.scope ||
    !!filters.expired ||
    filters.sort !== 'created_at' ||
    filters.order !== 'desc';

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-1 flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-600">
                <Ban className="h-4 w-4 text-white" />
              </div>
              <h1 className="text-2xl font-black text-gray-900">Messaging bans</h1>
            </div>
            <p className="text-sm text-gray-500">
              Restrict users or orgs from sending messages (enforced on live send paths).
            </p>
          </div>
          {canBan && (
            <Button type="button" className="gap-2" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              New ban
            </Button>
          )}
        </div>

        <div className="rounded-lg bg-white p-4 shadow">
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Filter by subject ID…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filters.subject_type}
              onChange={(e) => handleFilterChange('subject_type', e.target.value)}
              className={selectCls}
            >
              <option value="">All types</option>
              <option value="user">User</option>
              <option value="org">Org</option>
            </select>
            <select
              value={filters.active}
              onChange={(e) => handleFilterChange('active', e.target.value)}
              className={selectCls}
            >
              <option value="">Active: any</option>
              <option value="true">Active</option>
              <option value="false">Lifted</option>
            </select>
            <select
              value={filters.expired}
              onChange={(e) => handleFilterChange('expired', e.target.value)}
              className={selectCls}
            >
              <option value="">Expired: any</option>
              <option value="true">Past expires_at</option>
              <option value="false">Not expired</option>
            </select>
            <select
              value={filters.scope}
              onChange={(e) => handleFilterChange('scope', e.target.value)}
              className={selectCls}
            >
              <option value="">Scope: any</option>
              <option value="all">All</option>
              <option value="outbound">Outbound</option>
              <option value="dm">DM</option>
            </select>
            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              className={selectCls}
            >
              <option value="created_at">Sort: Created</option>
              <option value="expires_at">Sort: Expires</option>
              <option value="active">Sort: Active</option>
            </select>
            <select
              value={filters.order}
              onChange={(e) => handleFilterChange('order', e.target.value)}
              className={selectCls}
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load bans'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['Subject', 'Scope', 'Reason', 'Status', 'Expires', ''].map((h) => (
                    <th
                      key={h || 'actions'}
                      className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-20" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && bans.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-sm text-gray-500">
                      No bans found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  bans.map((ban: MessagingBan) => (
                    <tr key={ban.banid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {ban.subject_type === 'org' ? (
                          <OrgPartyLink oid={ban.subject_id} summary={ban.subject_summary} />
                        ) : (
                          <UserPartyLink uid={ban.subject_id} summary={ban.subject_summary} />
                        )}
                        <div className="mt-0.5 font-mono text-xs text-gray-400">
                          {ban.banid.slice(0, 12)}…
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{ban.scope ?? '—'}</td>
                      <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-600">
                        {ban.reason ?? '—'}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            ban.active
                              ? 'bg-red-50 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {ban.active ? 'Active' : 'Lifted'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {ban.expires_at ? formatEpoch(ban.expires_at) : 'Permanent'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {canBan && ban.active && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={liftBan.isPending}
                            onClick={async () => {
                              try {
                                await liftBan.mutateAsync({
                                  banid: ban.banid,
                                  idempotencyKey: generateIdempotencyKey(),
                                });
                                toast.success('Ban lifted', { richColors: true });
                              } catch (err: any) {
                                toast.error(err?.message || 'Failed to lift ban', {
                                  richColors: true,
                                });
                              }
                            }}
                          >
                            {liftBan.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Lift'
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons
              showSelect
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      {canBan && <CreateBanDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </div>
  );
};

export default MessagingBansPage;
